/**
 * Lightweight PKCE auth flow against Cognito Hosted UI.
 * Zero external dependencies — uses Web Crypto API.
 *
 * In local dev (cognitoDomain unset), auth is bypassed entirely.
 * In deployed environments, reads from runtime config.json (loaded at startup).
 */

import { getConfig } from './config';

function getAuthConfig() {
  const cfg = getConfig();
  return {
    domain: cfg.cognitoDomain || undefined,
    clientId: cfg.cognitoClientId || undefined,
    redirectUri: `${window.location.origin}/auth/callback`,
    scope: 'openid email profile',
  };
}

const TOKEN_KEY = 'sp_access_token';
const ID_TOKEN_KEY = 'sp_id_token';
const REFRESH_TOKEN_KEY = 'sp_refresh_token';
const CODE_VERIFIER_KEY = 'sp_code_verifier';

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ── Public API ────────────────────────────────────────────────────────────────

export function isLocalDev(): boolean {
  return !getAuthConfig().domain || getAuthConfig().domain === 'undefined';
}

export function getAccessToken(): string | null {
  if (isLocalDev()) return 'local-dev-token';
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getIdToken(): string | null {
  if (isLocalDev()) return null;
  return sessionStorage.getItem(ID_TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  if (isLocalDev()) return true;
  const token = getAccessToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export async function login(): Promise<void> {
  if (isLocalDev()) return;
  const verifier = generateCodeVerifier();
  sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
  const challenge = await generateCodeChallenge(verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: getAuthConfig().clientId!,
    redirect_uri: getAuthConfig().redirectUri!,
    scope: getAuthConfig().scope,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    identity_provider: 'Microsoft',
  });

  window.location.href = `${getAuthConfig().domain}/oauth2/authorize?${params}`;
}

/**
 * Sign in with Cognito username/password (USER_PASSWORD_AUTH flow).
 * Uses the Cognito Hosted UI /oauth2/token endpoint with password grant isn't supported,
 * so we use the InitiateAuth API directly via the public Cognito endpoint.
 */
export async function loginWithPassword(
  email: string,
  password: string,
): Promise<{ success: boolean; error?: string }> {
  const { clientId } = getAuthConfig();
  if (!clientId) return { success: false, error: 'Auth not configured' };

  // Cognito region is derived from the domain URL
  const domain = getAuthConfig().domain ?? '';
  // Domain format: https://<prefix>.auth.<region>.amazoncognito.com
  const regionMatch = domain.match(/\.auth\.([a-z0-9-]+)\.amazoncognito/);
  const region = regionMatch?.[1] ?? 'us-east-1';

  try {
    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const msg = data.message ?? data.__type ?? 'Sign in failed';
      return { success: false, error: msg };
    }

    if (data.AuthenticationResult) {
      sessionStorage.setItem(TOKEN_KEY, data.AuthenticationResult.AccessToken);
      sessionStorage.setItem(ID_TOKEN_KEY, data.AuthenticationResult.IdToken);
      if (data.AuthenticationResult.RefreshToken) {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, data.AuthenticationResult.RefreshToken);
      }
      return { success: true };
    }

    // Handle challenges (NEW_PASSWORD_REQUIRED, MFA, etc.)
    if (data.ChallengeName) {
      return { success: false, error: `Auth challenge: ${data.ChallengeName}. Please use SSO instead.` };
    }

    return { success: false, error: 'Unexpected response' };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}

export async function handleCallback(code: string): Promise<boolean> {
  const verifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  if (!verifier) return false;
  sessionStorage.removeItem(CODE_VERIFIER_KEY);

  const response = await fetch(`${getAuthConfig().domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: getAuthConfig().clientId!,
      code,
      redirect_uri: getAuthConfig().redirectUri!,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) return false;
  const tokens = await response.json();

  sessionStorage.setItem(TOKEN_KEY, tokens.access_token);
  sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  }
  return true;
}

/** Try to refresh tokens using the stored refresh token. Returns true on success. */
export async function refreshTokens(): Promise<boolean> {
  if (isLocalDev()) return true;

  const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  const { clientId, domain } = getAuthConfig();
  if (!clientId || !domain) return false;

  try {
    const response = await fetch(`${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) return false;
    const tokens = await response.json();

    sessionStorage.setItem(TOKEN_KEY, tokens.access_token);
    sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
    // Cognito doesn't rotate refresh tokens by default, but if it does:
    if (tokens.refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    }
    return true;
  } catch {
    return false;
  }
}

/** Returns seconds until the access token expires, or 0 if expired/missing. */
export function tokenExpiresIn(): number {
  if (isLocalDev()) return Infinity;
  const token = getAccessToken();
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Math.max(0, payload.exp - Math.floor(Date.now() / 1000));
  } catch {
    return 0;
  }
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  if (!isLocalDev()) {
    const params = new URLSearchParams({
      client_id: getAuthConfig().clientId!,
      logout_uri: window.location.origin,
    });
    window.location.href = `${getAuthConfig().domain}/logout?${params}`;
  }
}

/** Decode ID token to get user info (no signature verification — backend handles that) */
export function getCurrentUser(): { sub: string; email: string; name: string; groups: string[] } | null {
  if (isLocalDev()) {
    return { sub: 'local-dev-user', email: 'dev@localhost', name: 'Local Developer', groups: ['admin'] };
  }
  const token = getIdToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const rawGroups = payload['cognito:groups'];
    const groups: string[] = Array.isArray(rawGroups) ? rawGroups : [];
    return {
      sub: payload.sub,
      email: payload.email ?? '',
      name: payload.name ?? payload.given_name ?? payload.email ?? '',
      groups,
    };
  } catch {
    return null;
  }
}
